import React from "react";
import { CheckCircle2, Clock, MapPin, Package, ShieldCheck, Sparkles, Truck } from "lucide-react";
import { Link, useOutletContext } from "react-router-dom";

export default function ShippingPolicy() {
  const { settings } = useOutletContext();
  const appName = settings?.general?.appName || "Sugam Store";
  const defaultShipping = Number(settings?.orders?.defaultShippingCharge || 0);

  return (
    <main className="page-shell content-page policy-page">
      <div className="breadcrumbs">
        <Link to="/">Home</Link>
        <span>/</span>
        <span>Shipping & Delivery Policy</span>
      </div>

      <div className="policy-header">
        <span className="eyebrow">
          <Sparkles size={14} /> FAST & SAFE DISPATCH
        </span>
        <h1>Shipping & Delivery Policy</h1>
        <p className="policy-meta">Reliable express delivery to over 20,000+ pin codes across India</p>
      </div>

      <div className="shipping-highlights-grid">
        <div className="shipping-highlight-card">
          <Clock size={28} />
          <div>
            <strong>24 - 48 Hours Dispatch</strong>
            <p>Orders are packed and handed to couriers promptly.</p>
          </div>
        </div>
        <div className="shipping-highlight-card">
          <Truck size={28} />
          <div>
            <strong>Free Delivery Over ₹499</strong>
            <p>Standard delivery charges waived on qualifying orders.</p>
          </div>
        </div>
        <div className="shipping-highlight-card">
          <Package size={28} />
          <div>
            <strong>Tamper-Proof Packaging</strong>
            <p>Multi-layered protective boxes with security seals.</p>
          </div>
        </div>
        <div className="shipping-highlight-card">
          <MapPin size={28} />
          <div>
            <strong>Live SMS Tracking</strong>
            <p>Real-time updates directly via SMS and WhatsApp.</p>
          </div>
        </div>
      </div>

      <div className="policy-container">
        <aside className="policy-nav-card">
          <h3>Table of Contents</h3>
          <nav>
            <a href="#shipping-zones">1. Delivery Zones & Pin Codes</a>
            <a href="#shipping-timeline">2. Estimated Delivery Time</a>
            <a href="#shipping-charges">3. Shipping Fees & Free Shipping</a>
            <a href="#shipping-tracking">4. Live Order Tracking</a>
            <a href="#shipping-damage">5. Damaged or Tampered Parcels</a>
            <a href="#shipping-cod">6. Cash on Delivery (COD) Rules</a>
          </nav>
        </aside>

        <article className="policy-body">
          <section id="shipping-zones">
            <h2>1. Delivery Zones & Coverage</h2>
            <p>
              At <strong>{appName}</strong>, we ship to all major metros, tier-2, tier-3 cities, 
              and rural postal pin codes covering over 20,000+ destinations nationwide. 
              We partner with India's premier logistics providers including Blue Dart, Delhivery, 
              DTDC, Xpressbees, and India Post Speed Post.
            </p>
          </section>

          <section id="shipping-timeline">
            <h2>2. Estimated Shipping & Delivery Timelines</h2>
            <p>Once your order is successfully placed:</p>
            <ul>
              <li><strong>Order Processing & Dispatch:</strong> 24 to 48 business hours (excluding public holidays).</li>
              <li><strong>Metro & Tier-1 Cities:</strong> 2 to 4 business days post-dispatch.</li>
              <li><strong>Rest of India:</strong> 4 to 7 business days post-dispatch.</li>
              <li><strong>Remote & Island Regions:</strong> 7 to 10 business days.</li>
            </ul>
            <p className="policy-note">
              <em>Note:</em> Severe weather conditions, festivals, or regional operational restrictions may 
              occasionally cause slight delays. We will keep you proactively updated via SMS in such cases.
            </p>
          </section>

          <section id="shipping-charges">
            <h2>3. Shipping Charges & Free Shipping</h2>
            <ul>
              <li>
                <strong>Free Delivery:</strong> All prepaid and eligible orders totaling <strong>₹499 or more</strong> 
                qualify for free standard delivery!
              </li>
              <li>
                <strong>Orders under ₹499:</strong> A nominal delivery fee of 
                ₹{defaultShipping > 0 ? defaultShipping : 49} is applied at checkout to cover transit and packaging costs.
              </li>
              <li>
                The exact shipping fee (if applicable) is always transparently itemized on your cart summary prior to payment.
              </li>
            </ul>
          </section>

          <section id="shipping-tracking">
            <h2>4. Live Order Tracking</h2>
            <p>
              As soon as your parcel is scanned by the courier, you will receive an SMS and email notification 
              containing your unique Air Waybill (AWB) tracking number and live tracking link.
            </p>
            <p>
              You can check your order status from the <Link to="/orders">Orders page</Link> or use the live tracking link sent by SMS and email.
            </p>
          </section>

          <section id="shipping-damage">
            <h2>5. Damaged, Missing, or Tampered Parcels</h2>
            <p>
              We take great pride in our multi-layer protective packaging. However, if your package appears visibly 
              tampered with, damaged, or unsealed upon delivery:
            </p>
            <ul>
              <li>Please refuse to accept the parcel from the delivery agent, or</li>
              <li>Accept only after taking clear photographs/unboxing video of the condition.</li>
              <li>
                Contact our customer support within 24 hours at <Link to="/contact">Contact Us</Link> so we can 
                initiate an immediate replacement.
              </li>
            </ul>
          </section>

          <section id="shipping-cod">
            <h2>6. Cash on Delivery (COD) Guidelines</h2>
            <p>
              For Cash on Delivery orders, please ensure someone is available at the delivery address with the exact cash amount 
              or digital UPI option (supported by most courier delivery executives on arrival).
            </p>
          </section>
        </article>
      </div>
    </main>
  );
}
